/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <Teasers {...cmsData} />)
 * and then make these props fit the final module.
 */
import React from "react"
import { Section, CmsImage } from "@bw/bits"
import { Teasers as TeasersModule } from "@bw/modules"
import { Teaser, TeaserList } from "@bw/partials"
import { graphql } from "gatsby"
import useCollectionPages from "hooks/useCollectionPages"
import useCollectionArticles from "hooks/useCollectionArticles"
import useCollectionPois from "hooks/useCollectionPois"
import useCollectionEvents from "hooks/useCollectionEvents"
import useCollectionProfiles from "hooks/useCollectionProfiles"

const TeasersManual = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  idAttribute,
  classAttribute,
  related,
  numberOfColumns,
  isFollowedByModuleWithBackgroundVariant,
}) => {
  const nodes = [
    ...useCollectionPages(),
    ...useCollectionArticles(),
    ...useCollectionEvents(),
    ...useCollectionPois(),
    ...useCollectionProfiles(),
  ]

  const items = related
    .map(item => nodes.find(node => node.id === item.id))
    .filter(item => !!item)

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition,
        idAttribute,
        classAttribute,
        isFollowedByModuleWithBackgroundVariant,
      }}
    >
      <TeasersModule numberOfColumns={parseInt(numberOfColumns.value)}>
        {items.map(
          ({ id, title, subtitle, teaserExcerpt, teaserImage, url }, i) => {
            return numberOfColumns.value > 1 ? (
              <Teaser
                {...{ title, excerpt: teaserExcerpt ?? subtitle }}
                key={id}
                image={<CmsImage src={teaserImage} alt={teaserImage?.alt} />}
                to={url || '/'}
              />
            ) : (
              <TeaserList
                key={id}
                to={url || '/'}
                title={title}
                subtitle={teaserExcerpt ?? subtitle}
              />
            )
          }
        )}
      </TeasersModule>
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on TeasersManualModule {
 *   ...TeasersManualModuleFragment
 * }
 *
 * This is also what you will get in the props of your {{pascalCase name}}
 * resolver component.
 */
export const query = graphql`
  fragment TeasersManualModuleFragment on TeasersManualModule {
    related {
      id
    }
    numberOfColumns {
      value
    }
  }
`

export { TeasersManual }
